'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { $ModalStore, closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { generalEnv } from '@nx-frontend/shared/env';
import { $addonTranscribePrice, loadAddonTranscribePriceEvent } from '@nx-frontend/shared/store/addon-transcribe-price';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { GetTranscribePopupPayload, PopupType } from '@nx-frontend/shared/types';
import { OpenModalParams } from '@nx-frontend/shared/types';
import { mapPlanToSubscription } from '@nx-frontend/shared/utils';
import { getOpenPurchaseTranscribeBillingPopup } from './purchase-transcribe-billing-popup';

export interface GetTranscribePopupProps {
  openModal: (params: OpenModalParams) => void;
  payload?: GetTranscribePopupPayload;
}

export const getOpenTranscribePopup = ({ openModal, payload }: GetTranscribePopupProps) => {
  openModal({
    content: PopupType.GET_TRANSCRIBE,
    showCloseButton: false,
    modalContainerStyles: payload?.isAddonExists ? '' : 'bg-[#002730]',
    payload,
  });
};

const transcribePrice = {
  monthly: 10,
  yearly: 7,
};

export function GetTranscribePopup() {
  const [
    currentOrganization,
    billingInfo,
    addonTranscribePrice,
    openModal,
    closeModal,
    loadAddonTranscribePrice,
    modalStore,
  ] = useUnit([
    $currentOrganization,
    $billingInfo,
    $addonTranscribePrice,
    openModalEvent,
    closeModalEvent,
    loadAddonTranscribePriceEvent,
    $ModalStore,
  ]);

  const modal = modalStore.find((m) => m.content === PopupType.GET_TRANSCRIBE);
  const payload = modal?.payload as GetTranscribePopupPayload;
  const subscriptionPeriod = mapPlanToSubscription(billingInfo).period as keyof typeof transcribePrice;

  useEffect(() => {
    if (!addonTranscribePrice.isLoaded && billingInfo.subscriptionId) {
      // lazy load addon transcribe prices
      loadAddonTranscribePrice({
        orgId: currentOrganization.globalId,
        subscriptionId: billingInfo.subscriptionId,
        quantity: 1,
      });
    }
  }, [currentOrganization, billingInfo, addonTranscribePrice.isLoaded, loadAddonTranscribePrice]);

  const confirmClick = useCallback(async () => {
    if (addonTranscribePrice.isLoaded) {
      // waiting until transcribe limit popup close then open transcribe billing confirm popup
      setTimeout(() => {
        getOpenPurchaseTranscribeBillingPopup({
          openModal,
          payload: {
            payloadType: PopupType.PURCHASE_TRANSCRIBE_BILLING,
          },
        });
      }, 250);
      closeModal();
    }
  }, [openModal, closeModal, addonTranscribePrice.isLoaded]);

  const bookDemoClick = useCallback(async () => {
    window.open(generalEnv.bookDemoUrl + '/?ws=transcribe_billing_popup', '_blank');
    closeModal();
  }, [closeModal]);

  const onQuantityChange = useCallback(
    (value: number) => {
      if (billingInfo.subscriptionId) {
        loadAddonTranscribePrice({
          orgId: currentOrganization.globalId,
          subscriptionId: billingInfo.subscriptionId,
          quantity: value,
        });
      }
    },
    [currentOrganization, billingInfo, loadAddonTranscribePrice]
  );

  if (!payload) {
    return null;
  }

  const hasAddonPopup = (
    <div className="flex flex-col text-center">
      <div
        className="cursor-pointer text-gray-400 hover:text-gray-700 absolute top-[8px] right-[8px]"
        style={{ width: 18, height: 18 }}
        onClick={closeModal}
      >
        <Icon icon={IconsMap.CLOSE} />
      </div>
      <div className="bg-[#092d36] py-1 px-3 mb-4 rounded-md">
        <img src="/assets/img/transcript-limit-ltd.png" alt="" width="384" height="225" />
      </div>
      <h2 className="font-semibold text-lg mb-2 text-left">Do you need extra transcription hours?</h2>
      <p className="text-left mb-2">Extend your monthly package with:</p>
      <div className="relative w-full mb-3">
        <TranscribePopupSelect
          onChange={onQuantityChange}
          options={Array(5)
            .fill('')
            .map((_, index) => {
              const i = index + 1;
              return { value: i, label: `+${20 * i} hours for ${transcribePrice[subscriptionPeriod] * i} $ / monthly` };
            })}
        />
      </div>
      <div>
        <Button
          type={ButtonType.FLAT}
          onClick={confirmClick}
          bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
          radius="rounded"
          data-cy={
            'unlock-transcribe-get-addon-btn-' + (addonTranscribePrice.isLoaded ? 'price-loaded' : 'price-not-loaded')
          }
          className="m-h-[36px] h-[36px] w-full"
        >
          {!addonTranscribePrice.isLoaded ? (
            <Icon icon={IconsMap.LOADER} className="animate-spin" />
          ) : (
            'Add to the current plan'
          )}
        </Button>
      </div>
    </div>
  );

  const noAddonPupup = (
    <div
      data-cy="unlock-transcribe-popup"
      className="px-2 py-[0] w-[970px] min-w-[970px] min-h-[400px] relative nimbus-popup-content mx-1"
    >
      <div className="flex flex-col relative">
        <div
          className="cursor-pointer mb-2 text-gray-400 hover:text-gray-700 absolute -top-[17px] -right-[15px]"
          style={{ width: 18, height: 18 }}
          onClick={closeModal}
        >
          <Icon icon={IconsMap.CLOSE} />
        </div>
        <div
          data-cy="unlock-transcribe-popup-title"
          className="items-center basis-full w-full text-center text-3xl font-semibold text-white mb-[32px]"
        >
          Unlock the Magic of Video Transcription Now!
        </div>
        <div className="flex">
          <div className="w-full mr-[20px]">
            <div className="bg-[#FADBD7] m-h-[59px] pt-[14px] pb-[16px] px-[16px] flex flex-col align-middle mb-[16px] rounded-lg">
              <div className="flex justify-between m-h-[21px] mb-[8px] text-sm">
                <div>Ooops!</div>
                <div>
                  <span className="font-semibold">
                    {`${Math.ceil(payload.usage.current / 60)}/${Math.ceil(payload.usage.max / 60)} hour`}
                  </span>{' '}
                  of Transcribe used
                </div>
              </div>
              <div className="w-full bg-[#E35141] h-[6px] rounded-lg"></div>
            </div>
            <div className="flex flex-row bg-white rounded-lg px-[24px] pb-[32px] pt-[20px] min-h-[222px] items-end">
              <div className="mr-[14px]">
                <div className="mt-[8px]">
                  <div className="pb-[14px] text-xl font-semibold">Get FuseBase Transcribe</div>
                </div>
                <div className="flex flex-row align-middle mb-[16px]">
                  <div className="text-3xl font-semibold text-[#176B87] mr-[8px]">{`$${transcribePrice[subscriptionPeriod]}`}</div>
                  <div className="text-xs text-black/30 pt-[2px]">
                    <div>per addon per month</div>
                    <div>with annual plan</div>
                  </div>
                </div>
                <div className="relative max-w-[192px] mb-3">
                  <TranscribePopupSelect
                    onChange={onQuantityChange}
                    options={Array(5)
                      .fill('')
                      .map((_, index) => {
                        const i = index + 1;
                        return { value: i, label: `${i} addon${i > 1 ? 's' : ''} (${20 * i} hours)` };
                      })}
                  />
                </div>
                <div>
                  <Button
                    type={ButtonType.FLAT}
                    onClick={confirmClick}
                    bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
                    radius="rounded"
                    data-cy={
                      'unlock-transcribe-get-addon-btn-' +
                      (addonTranscribePrice.isLoaded ? 'price-loaded' : 'price-not-loaded')
                    }
                    className="m-h-[36px] h-[36px] w-[192px]"
                  >
                    {!addonTranscribePrice.isLoaded ? (
                      <Icon icon={IconsMap.LOADER} className="animate-spin" />
                    ) : (
                      'Get Transcribe addon'
                    )}
                  </Button>
                </div>
              </div>
              <div>
                <img
                  src="/assets/img/unlock-transcribe-buttons.png"
                  width={286}
                  height={169}
                  className="min-h-[169px]"
                  alt=""
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg min-w-[318px] w-[318px] p-[24px]">
            <div className="mb-[10px]">
              <img
                src="/assets/img/unlock-ai-book-call.svg"
                width={127}
                height={132}
                className="rounded-sm min-h-[132px]"
                alt=""
              />
            </div>
            <div className="text-lg text-black font-semibold mb-[8px] whitespace-nowrap">
              <span>Are You Ready to </span>
              <span className="text-[#176B87]">Wow Clients?</span>
            </div>
            <div className="text-sm mb-[10px]">
              Book a call to see how tailored portals increase client satisfaction.
            </div>
            <div>
              <Button
                type={ButtonType.FLAT}
                onClick={bookDemoClick}
                bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
                radius="rounded"
                isFull={true}
                data-cy="unlock-transcribe-book-call-btn"
                className="m-h-[30px] h-[30px]"
              >
                Book a demo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return payload.isAddonExists ? hasAddonPopup : noAddonPupup;
}

function TranscribePopupSelect({
  onChange,
  options,
}: {
  onChange: (value: number) => void;
  options: { value: number; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(options[0]);
  const domRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onContainerMouseDown = (event: MouseEvent) => {
      if (event.target === domRef.current || domRef.current?.contains(event.target as Node | null)) {
        return;
      }
      setOpen(false);
    };

    if (open) {
      const container = document.getElementById('ModalContainer');
      if (container) {
        container.addEventListener('mousedown', onContainerMouseDown);
        return () => {
          container.removeEventListener('mousedown', onContainerMouseDown);
        };
      }
    }
  }, [open]);

  const onSelectClick = () => {
    setOpen(!open);
  };

  const onSelectChange = ({ value, label }: { value: number; label: string }) => {
    onChange(value);
    setCurrent({ value, label });
    setOpen(false);
  };

  return (
    <div className="fc-header-select relative z-10 select-none" ref={domRef}>
      <div
        className={classNames(
          'px-2.5 py-1 rounded border border-neutral-200 cursor-pointer flex items-center justify-between',
          {
            'hover:bg-neutral-200': !open,
          }
        )}
        onClick={onSelectClick}
      >
        <span className="fc-header-select-current block whitespace-nowrap">{current.label}</span>
        <Icon icon={IconsMap.ARROW_DOWN} className="w-4 text-neutral-500 flex-none" />
      </div>
      <div
        className={classNames('w-full absolute top-full left-0 rounded border border-neutral-200 bg-white text-left', {
          hidden: !open,
        })}
      >
        {options.map((option) => (
          <div
            className="px-2.5 py-1 hover:bg-neutral-200 cursor-pointer"
            onClick={() => onSelectChange(option)}
            key={option.value + '-transcribe-value'}
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
  );
}
